/**
 * <zg-layer-comparison>: progressive enhancement for sections/zg-layer-comparison.liquid.
 *
 * The section renders the finished regular-filter state: exploded view at rest, the first
 * toggle pressed, no particles. This element adds
 * - the reveal: the frame and the support start pressed onto the media and slide apart once
 *   30% of the illustration is in view (Replay runs it again);
 * - the coated state: `is-coated` on this element, which CSS turns into the media colours,
 *   marker 03, and the highlighted coating layer, plus the coating sheet dropping in (`data-ghost`);
 * - airflow lines and particles on the canvas: a particle that reaches the media is caught
 *   with probability RATE_ON (coated) or RATE_OFF (regular), stays LIFE seconds, then fades.
 *
 * Ported from the design's React class ("ZenGUARD Layer Section"): the constants, polygons,
 * catch rates and timings are the design's. Geometry is in SVG viewBox units (680 x 760).
 * With prefers-reduced-motion there is no slide, coating sheet, particles or automatic switch;
 * the toggle still recolours. The canvas loop runs only while the illustration is on screen
 * and the tab is visible.
 *
 * WCAG 2.2.2: the Pause button stops the animation (no particles, airflow or transitions; the
 * toggle still recolours, instantly). It also stops by itself after AUTO_STOP_AFTER seconds of
 * running without interaction: nothing new spawns, falling particles finish, then the loop ends
 * and the button offers Play. A paused animation stays paused when scrolled back into view;
 * Play or Replay starts it again.
 */

/** Seconds a caught particle stays before it fades. */
const LIFE = 16;
/** Seconds a caught particle takes to fade out. */
const FADE = 0.6;
const RATE_ON = 0.75;
const RATE_OFF = 0.1;
/** Fall speed, viewBox units per second. */
const SPEED = 70;
/** Particles per second at a density of 1 (the setting's 100%). */
const SPAWN_RATE = 7;
const VIEW_WIDTH = 680;
const VIEW_HEIGHT = 760;
/** x positions of the dashed airflow lines. */
const LANES = [150, 200, 250, 300, 350, 400, 450];
const DASH = [20, 36];
const DASH_PERIOD = 56;
/** Front lip of the cardboard frame: particles and airflow pass behind it. */
const FRAME_FRONT = [
  [79.5, 220],
  [79.5, 232],
  [339.3, 382],
  [529.8, 272],
  [529.8, 260],
  [502.1, 260],
  [339.3, 354],
  [107.2, 220],
];
/** Outline of the pleated media: particles that pass through are hidden inside it. */
const MEDIA_BODY = [
  [270, 250],
  [529.8, 400],
  [529.8, 424],
  [339.3, 534],
  [79.5, 384],
  [79.5, 360],
];

/** Share of the illustration that must be visible to start the reveal. */
const REVEAL_RATIO = 0.3;
/** ms; used only where IntersectionObserver is unavailable. */
const REVEAL_FALLBACK = 3000;
/** ms after the reveal before switching to the coated filter, unless the shopper already chose. */
const AUTO_DEMO_DELAY = 5400;
/** ms between Replay folding the filter up and the reveal starting again. */
const REPLAY_DELAY = 900;
/** ms after the reveal before particles start falling. */
const PARTICLES_AFTER = 1250;
/** ms after the reveal before the airflow lines fade in, and how long they take (in and out). */
const AIR_IN_AFTER = 400;
const AIR_IN_DURATION = 900;
/** Seconds of running without interaction after which the animation stops by itself. */
const AUTO_STOP_AFTER = 20;
/** Longest simulated step in seconds, so a resumed loop doesn't spawn a burst. */
const MAX_STEP = 0.05;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Design names in brackets.
 * @typedef {object} Particle
 * @property {number} x - Horizontal position [x].
 * @property {number} top - Start height above 0 [y0].
 * @property {number} fallen - Distance fallen so far [d].
 * @property {number} catchAt - Distance at which it meets the media [stop].
 * @property {number} end - Distance at which it leaves the view [end].
 * @property {number} roll - Random number compared with the catch rate [rr].
 * @property {number} jitter - Horizontal offset once caught [j].
 * @property {boolean} stuck - Caught by the media.
 * @property {boolean} passed - Went through the media.
 * @property {number} stuckFor - Seconds since it was caught [st].
 * @property {number} alpha - Current opacity [a].
 */

class ZgLayerComparison extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLElement | null} */
  #figure = null;
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  #context = null;
  /** @type {HTMLElement | null} */
  #note = null;
  /** @type {HTMLButtonElement[]} */
  #toggles = [];
  /** @type {HTMLButtonElement | null} */
  #replayButton = null;
  /** @type {HTMLButtonElement | null} */
  #pauseButton = null;
  /** @type {Particle[]} */
  #particles = [];
  #coated = false;
  #revealed = false;
  /** When the airflow last started (reveal or Play); lines fade in and particles start from here. */
  #flowStartedAt = 0;
  #touched = false;
  #inView = false;
  #paused = false;
  /** Paused by the automatic stop and still letting falling particles finish. */
  #windingDown = false;
  #windDownAt = 0;
  /** Seconds the animation has run since the last interaction. */
  #runTime = 0;
  #spawnDebt = 0;
  #lastFrameTime = 0;
  #frame = 0;
  #ghostFrame = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #demoTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #revealTimer;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#figure = this.querySelector('.zg-layer-comparison__figure');
    this.#canvas = this.querySelector('canvas.zg-layer-comparison__canvas');
    this.#context = this.#canvas?.getContext('2d') ?? null;
    this.#note = this.querySelector('.zg-layer-comparison__note');
    this.#toggles = Array.from(this.querySelectorAll('button[data-coat]'));
    this.#replayButton = this.querySelector('button[data-replay]');
    this.#pauseButton = this.querySelector('button[data-pause]');
    this.#coated = this.classList.contains('is-coated');

    for (const button of this.#toggles) {
      button.addEventListener('click', () => this.#setCoated(button.dataset.coat === 'true', true), { signal });
    }
    this.#replayButton?.addEventListener('click', () => this.replay(), { signal });
    this.#pauseButton?.addEventListener('click', () => this.#togglePaused(), { signal });
    document.addEventListener('visibilitychange', () => this.#syncLoop(), { signal });
    reducedMotion.addEventListener('change', () => this.#restart(), { signal });

    this.#start();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#stop();
  }

  /** Fold the filter up, switch back to the regular filter, and reveal again (the Replay button). */
  replay() {
    if (reducedMotion.matches) return;

    clearTimeout(this.#demoTimer);
    clearTimeout(this.#revealTimer);
    // Un-pausing first switches transitions back on, so the fold below animates.
    this.#setPaused(false);
    this.#touched = false;
    this.#particles = [];
    this.#spawnDebt = 0;
    this.#revealed = false;
    this.classList.add('is-collapsed');
    this.#applyCoated(false);
    this.#syncLoop();
    this.#revealTimer = setTimeout(() => this.#reveal(), REPLAY_DELAY);
  }

  #start() {
    if (this.#replayButton) this.#replayButton.hidden = reducedMotion.matches;
    this.#renderPauseButton();
    if (reducedMotion.matches || !this.#figure) return;

    // Don't fold up an illustration the shopper (or the theme editor) is already looking at.
    if (window.Shopify?.designMode || visibleRatio(this.#figure) >= REVEAL_RATIO) {
      this.#reveal();
    } else {
      this.classList.add('is-collapsed');
    }
    // Commit the starting position before switching transitions on, so it doesn't animate.
    this.#figure.getBoundingClientRect();
    if (!this.#paused) this.classList.add('is-animated');

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: [0, REVEAL_RATIO] });
      this.#observer.observe(this.#figure);
    } else {
      this.#inView = true;
      this.#revealTimer = setTimeout(() => this.#reveal(), REVEAL_FALLBACK);
      this.#syncLoop();
    }
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#demoTimer);
    clearTimeout(this.#revealTimer);
    cancelAnimationFrame(this.#ghostFrame);
    this.#ghostFrame = 0;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** The reduced-motion preference changed: settle at rest, then start again under the new preference. */
  #restart() {
    this.#stop();
    this.#particles = [];
    this.#clearCanvas();
    this.#revealed = false;
    this.#inView = false;
    this.#windingDown = false;
    this.classList.remove('is-animated', 'is-collapsed');
    this.dataset.ghost = '0';
    this.#start();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    for (const entry of entries) {
      this.#inView = entry.isIntersecting;
      if (entry.isIntersecting && entry.intersectionRatio >= REVEAL_RATIO) this.#reveal();
    }
    this.#syncLoop();
  };

  #reveal() {
    if (this.#revealed) return;

    this.#revealed = true;
    this.#flowStartedAt = performance.now();
    this.classList.remove('is-collapsed');

    if (this.dataset.autoDemo !== 'false' && !this.#paused) {
      clearTimeout(this.#demoTimer);
      this.#demoTimer = setTimeout(() => {
        if (!this.#touched && !this.#paused) this.#setCoated(true, false);
      }, AUTO_DEMO_DELAY);
    }
    this.#syncLoop();
  }

  /** The Pause / Play button. */
  #togglePaused() {
    this.#touched = true;
    this.#setPaused(!this.#paused);
    this.#syncLoop();
  }

  /**
   * Paused means no motion at all: no particles or airflow, no transitions, no coating sheet,
   * no automatic switch. Callers outside the animation frame call #syncLoop afterwards.
   * @param {boolean} value
   * @param {boolean} [windDown] - The automatic stop: let falling particles finish first.
   * @param {number} [time] - Current rAF or performance.now() time.
   */
  #setPaused(value, windDown = false, time = performance.now()) {
    this.#paused = value;
    this.#windingDown = value && windDown;
    this.#windDownAt = time;
    this.#runTime = 0;

    if (value) {
      clearTimeout(this.#demoTimer);
      cancelAnimationFrame(this.#ghostFrame);
      this.#ghostFrame = 0;
      this.dataset.ghost = '0';
      this.classList.remove('is-animated');
    } else if (!reducedMotion.matches) {
      this.classList.add('is-animated');
      this.#flowStartedAt = time;
    }
    this.#renderPauseButton();
  }

  #renderPauseButton() {
    const button = this.#pauseButton;
    if (!button) return;

    button.hidden = reducedMotion.matches;
    // Play/pause pattern: the label states the action; no aria-pressed (it would read "Play animation, pressed").
    button.textContent = (this.#paused ? button.dataset.labelPlay : button.dataset.labelPause) ?? '';
  }

  /**
   * @param {boolean} value - Show the coated filter.
   * @param {boolean} byUser - A shopper pressed a toggle; cancels the automatic switch.
   */
  #setCoated(value, byUser) {
    if (byUser) {
      this.#touched = true;
      this.#runTime = 0;
      clearTimeout(this.#demoTimer);
    }
    if (this.#coated === value) return;

    // Particles already caught start fading, so the new catch rate shows straight away.
    for (const particle of this.#particles) {
      if (particle.stuck) particle.stuckFor = Math.max(particle.stuckFor, LIFE);
    }
    this.#applyCoated(value);

    if (value && !reducedMotion.matches && !this.#paused) {
      // The coating sheet shows above the media for one frame, then drops onto it and fades.
      this.dataset.ghost = '1';
      this.#ghostFrame = requestAnimationFrame(() => {
        this.#ghostFrame = requestAnimationFrame(() => {
          this.#ghostFrame = 0;
          this.dataset.ghost = this.#coated ? '2' : '0';
        });
      });
    }
  }

  /** @param {boolean} value */
  #applyCoated(value) {
    this.#coated = value;
    cancelAnimationFrame(this.#ghostFrame);
    this.#ghostFrame = 0;
    this.dataset.ghost = '0';
    this.classList.toggle('is-coated', value);

    for (const button of this.#toggles) {
      button.setAttribute('aria-pressed', String((button.dataset.coat === 'true') === value));
    }
    if (this.#note) {
      this.#note.textContent = (value ? this.#note.dataset.noteOn : this.#note.dataset.noteOff) ?? '';
    }
  }

  get #particlesEnabled() {
    return this.dataset.particles !== 'false' && !reducedMotion.matches;
  }

  get #density() {
    return Number(this.dataset.particleDensity) / 100 || 1.5;
  }

  /**
   * Run the canvas loop only while it can be seen and has something to draw. A paused animation
   * never restarts here (only Play or Replay un-pause it); one that is paused and not running is cleared.
   */
  #syncLoop() {
    const visible =
      this.#revealed && this.#particlesEnabled && this.#inView && !document.hidden && this.#context !== null;
    const run = visible && (!this.#paused || this.#windingDown);

    if (run && !this.#frame) {
      this.#lastFrameTime = 0;
      this.#frame = requestAnimationFrame(this.#tick);
    } else if (!run && this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }

    if (!this.#revealed || (this.#paused && !run)) {
      this.#windingDown = false;
      this.#particles = [];
      this.#clearCanvas();
    }
  }

  /** @param {number} time */
  #tick = (time) => {
    const elapsed = this.#lastFrameTime ? Math.max(0, time - this.#lastFrameTime) / 1000 : 0;
    this.#lastFrameTime = time;

    if (!this.#paused) {
      this.#runTime += elapsed;
      if (this.#runTime >= AUTO_STOP_AFTER) this.#setPaused(true, true, time);
    }

    const busy = this.#draw(Math.min(MAX_STEP, elapsed), time);
    if (this.#paused && !busy) {
      // The automatic stop has finished winding down.
      this.#frame = 0;
      this.#windingDown = false;
      this.#particles = [];
      this.#clearCanvas();
      return;
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #clearCanvas() {
    if (!this.#canvas || !this.#context) return;
    this.#context.setTransform(1, 0, 0, 1, 0, 0);
    this.#context.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
  }

  /**
   * Advance the particles by `step` seconds and draw one frame.
   * @param {number} step
   * @param {number} time - rAF timestamp, same clock as performance.now().
   * @returns {boolean} Whether anything is still moving; false once a wind-down has finished.
   */
  #draw(step, time) {
    const canvas = this.#canvas;
    const context = this.#context;
    if (!canvas || !context) return false;

    const pixelRatio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const pixelWidth = Math.round(width * pixelRatio);
    const pixelHeight = Math.round(canvas.clientHeight * pixelRatio);
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    this.#clearCanvas();
    if (!width) return !this.#paused;

    const scale = (width / VIEW_WIDTH) * pixelRatio;
    context.setTransform(scale, 0, 0, scale, 0, 0);

    const since = time - this.#flowStartedAt;
    if (since > PARTICLES_AFTER && !this.#paused) {
      this.#spawnDebt += step * SPAWN_RATE * this.#density;
      while (this.#spawnDebt >= 1) {
        this.#spawnDebt -= 1;
        this.#particles.push(spawn());
      }
    }

    const rate = this.#coated ? RATE_ON : RATE_OFF;
    /** @type {Particle[]} */
    const below = [];
    /** @type {Particle[]} */
    const above = [];

    for (let index = this.#particles.length - 1; index >= 0; index--) {
      const particle = this.#particles[index];
      if (!particle) continue;
      // Winding down: caught particles fade now instead of staying for LIFE seconds.
      if (this.#paused && particle.stuck) particle.stuckFor = Math.max(particle.stuckFor, LIFE);

      if (particle.stuck) {
        particle.stuckFor += step;
      } else {
        particle.fallen += SPEED * step;
        if (!particle.passed && particle.fallen >= particle.catchAt) {
          if (particle.roll < rate) {
            particle.fallen = particle.catchAt;
            particle.stuck = true;
            particle.stuckFor = 0;
          } else {
            particle.passed = true;
          }
        }
      }

      let alpha = Math.min(1, particle.fallen / 30);
      if (particle.stuck) {
        alpha = particle.stuckFor < LIFE ? 1 : 1 - (particle.stuckFor - LIFE) / FADE;
      } else if (particle.fallen > particle.end - 90) {
        alpha = Math.max(0, (particle.end - particle.fallen) / 90);
      }

      if (alpha <= 0 || (!particle.stuck && particle.fallen >= particle.end)) {
        this.#particles.splice(index, 1);
        continue;
      }
      particle.alpha = alpha;
      (particle.passed ? below : above).push(particle);
    }

    for (const particle of below) dot(context, particle, '#4a4d54', 0.5, 3, 0);
    erase(context, MEDIA_BODY);

    const airIn = Math.min(1, Math.max(0, (since - AIR_IN_AFTER) / AIR_IN_DURATION));
    const airOut = this.#paused ? Math.min(1, Math.max(0, 1 - (time - this.#windDownAt) / AIR_IN_DURATION)) : 1;
    if (airIn * airOut > 0) {
      context.globalAlpha = 0.55 * airIn * airOut;
      context.lineWidth = 1.4;
      context.lineCap = 'round';
      context.strokeStyle = '#8e9298';
      context.setLineDash(DASH);
      LANES.forEach((x, lane) => {
        context.lineDashOffset = -(((time / 1000) * SPEED + lane * 23) % DASH_PERIOD);
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x, VIEW_HEIGHT);
        context.stroke();
      });
      context.setLineDash([]);
    }

    for (const particle of above) {
      if (particle.stuck) dot(context, particle, '#1e1f22', 1, 3.8, particle.jitter);
      else dot(context, particle, '#4a4d54', 0.85, 3, 0);
    }
    erase(context, FRAME_FRONT);
    context.globalAlpha = 1;

    return !this.#paused || this.#particles.length > 0 || airIn * airOut > 0;
  }
}

/**
 * A particle starting above a random point of the media's top face.
 * @returns {Particle}
 */
function spawn() {
  const u = 30 + Math.random() * 240;
  const v = 30 + Math.random() * 160;
  const top = Math.random() * 30;
  return {
    x: 270 + 0.866 * (u - v),
    top,
    fallen: 0,
    catchAt: 250 + 0.5 * (u + v) - top,
    end: VIEW_HEIGHT - top,
    roll: Math.random(),
    jitter: (Math.random() - 0.5) * 6,
    stuck: false,
    passed: false,
    stuckFor: 0,
    alpha: 0,
  };
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {Particle} particle
 * @param {string} color
 * @param {number} opacity
 * @param {number} radius
 * @param {number} offsetX
 */
function dot(context, particle, color, opacity, radius, offsetX) {
  context.fillStyle = color;
  context.globalAlpha = particle.alpha * opacity;
  context.beginPath();
  context.arc(particle.x + offsetX, particle.top + particle.fallen, radius, 0, Math.PI * 2);
  context.fill();
}

/**
 * Clear a polygon so the SVG artwork beneath shows through (things pass "behind" it).
 * @param {CanvasRenderingContext2D} context
 * @param {number[][]} points
 */
function erase(context, points) {
  context.save();
  context.globalCompositeOperation = 'destination-out';
  context.globalAlpha = 1;
  context.fillStyle = '#000';
  context.beginPath();
  points.forEach(([x = 0, y = 0], index) => (index ? context.lineTo(x, y) : context.moveTo(x, y)));
  context.closePath();
  context.fill();
  context.restore();
}

/**
 * Share of an element's height inside the viewport, 0 to 1.
 * @param {Element} element
 */
function visibleRatio(element) {
  const { top, bottom, height } = element.getBoundingClientRect();
  if (height <= 0) return 0;
  return Math.max(0, Math.min(bottom, window.innerHeight) - Math.max(top, 0)) / height;
}

if (!customElements.get('zg-layer-comparison')) {
  customElements.define('zg-layer-comparison', ZgLayerComparison);
}
